import { FC } from 'react'
import { Box } from '@/components/atoms/Grid'
import { ImageLabel, ProductLabelShape } from '@/network/graphql.g'
import styled from 'styled-components'
import { validFor } from '@/theme/validFor'
import { ProductLabelStyle } from '../organisms/productLabels'
import { DetailTextLabel, ListingTextLabel } from './productLabel'

type ProductBadgeProps = {
  badge: ImageLabel
  labelStyle: ProductLabelStyle
  isDetail: boolean
} & Rest

const ProductBadge: FC<ProductBadgeProps> = ({
  badge,
  labelStyle,
  isDetail,
  ...rest
}) =>
  badge ? (
    <>
      {badge?.badgeText ? (
        isDetail ? (
          <StyleTextBoxDetail
            backgroundColor={badge?.backgroundColor}
            fontColor={badge?.fontColor}
          >
            {badge?.badgeText}
          </StyleTextBoxDetail>
        ) : (
          <StyleTextBox
            backgroundColor={badge?.backgroundColor}
            fontColor={badge?.fontColor}
          >
            {badge?.badgeText}
          </StyleTextBox>
        )
      ) : (
        <StyledBox badge={badge} textAlign="right" {...rest}>
          {badge?.shape === ProductLabelShape.Square ? ( // Assuming 'ProductLabelShape.Square' resolves to 'Square'
            <StyledBoxSquare>
              <StyledImgSquare
                src={badge?.imgSrc}
                labelStyle={labelStyle}
                multiplySquareHeight={
                  badgeCondition(badge?.imgSrc)
                    ? { wide: 2, desktop: 2.4, tablet: 2.7, mobile: 3 }
                    : { wide: 1, desktop: 1, tablet: 1, mobile: 1 }
                }
              />
            </StyledBoxSquare>
          ) : (
            <StyledImgRectangle
              src={badge?.imgSrc}
              shape={badge?.shape}
              labelStyle={labelStyle}
            />
          )}
        </StyledBox>
      )}
    </>
  ) : null

export default ProductBadge

const badgeCondition = (src: string) => src.match(/badges\/241\//)

export const filterBadges = (
  badges: ImageLabel[],
  include: boolean
): ImageLabel[] =>
  badges?.filter(
    (item) =>
      (include && badgeCondition(item.imgSrc)) ||
      (!include && !badgeCondition(item.imgSrc))
  ) ?? []

const StyleTextBoxDetail = styled(DetailTextLabel)`
  background-color: ${(props) =>
    props.backgroundColor || props.theme.colors.onBackground};
  color: ${(props) => props.fontColor || props.theme.colors.background};
  display: inline-block;
  white-space: nowrap;
  text-transform: none;
`

const StyleTextBox = styled(ListingTextLabel)`
  background-color: ${(props) =>
    props.backgroundColor || props.theme.colors.onBackground};
  color: ${(props) => props.fontColor || props.theme.colors.background};
  display: inline-block;
  white-space: nowrap;
  text-transform: none;
`

const StyledBox = styled(Box)`
  margin-bottom: 5px;
  &:last-child {
    margin-bottom: 0;
  }
`

const StyledBoxSquare = styled(Box)`
  margin-top: -5px;
`

const StyledImgSquare = styled.img`
  display: block;
  max-width: 88px;
  height: ${({ multiplySquareHeight }) => 50 * multiplySquareHeight.desktop}px;
  ${validFor.tablet`      
    height: ${({ multiplySquareHeight }) => 45 * multiplySquareHeight.tablet}px;
    max-width: 68px;
  `}
  ${validFor.mobile`      
    height: ${({ multiplySquareHeight }) =>
      40 * multiplySquareHeight.mobile}px;   
    max-width: 68px;
  `}
  ${validFor.wide`      
    height: ${({ multiplySquareHeight }) => 60 * multiplySquareHeight.wide}px;
  `}
  border-radius: ${(props) => props.theme.borderRadius}px;
`

const StyledImgRectangle = styled.img`
  display: block;
  max-width: 88px;
  height: 22px;
  ${validFor.tablet`      
    height: 17px;
    max-width: 68px;
  `}
  ${validFor.mobile`      
    height: 17px;   
    max-width: 68px;
  `}
  ${validFor.wide`      
    height: 22px;
  `}
  border-radius: ${(props) => props.theme.borderRadius}px;
`
